import { useReactiveVar } from "@apollo/client";
import React, { useState } from "react";
import { useHistory } from "react-router";

import { address } from "/apollo/client/local";
import { AddressState } from "/apollo/client/local/address/address.types";
import LogoWordMark from "/asset/image/cityMDLogoNew.png";
import Button from "/component/base/Button";
import InfoBanner from "/component/base/InfoBanner/InfoBanner";
import Text from "/component/base/Text";
import TextDivider from "/component/base/TextDivider";
import Tooltip from "/component/base/Tooltip";
import AddressCombobox from "/component/partial/AddressCombobox";
import { useToastContext } from "/component/provider/ToastProvider";
import routes from "/constant/url.constant";
import { getAddressCurrentLocation, updateAddress as geoServiceUpdateAddress } from "/geoservice";
import { useInfoBanners, useTranslation } from "/hook";
import useAuth from "/hook/useAuth";

import SendPasswordResetModal from "../SendPasswordResetModal";
import {
  Description,
  Modal,
  StepOneContent,
  StepOneFooter,
  StepOneFooterVertical,
  StepTwoContent,
  StepTwoFooter,
  StepTwoHeader,
  StepTwoSubtitle,
  StepTwoTitle,
  Title,
} from "./OnboardingModal.styles";

const SIGN_UP_V2_ENABLED = process.env.SIGN_UP_V2_ENABLED === "true";

interface Props {
  onClose?: () => void;
  redirectPage?: string;
}

const OnboardingModal = ({ redirectPage, onClose }: Props) => {
  const { login } = useAuth();
  const { t } = useTranslation("onboarding");
  const { showToast } = useToastContext();
  const history = useHistory();
  const { enabled, showIcon, messageText, linkText, link, variant } =
    useInfoBanners("onboarding_modal");

  const addressStored = useReactiveVar<AddressState>(address.var);

  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [onBoardingModalStep, setOnboardingModalStep] = useState(0);
  const [onboardingModalIsOpen, setOnboardingModalIsOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState<boolean | string>(false);

  const updateAddress = (placeDetails: AddressState) => {
    setIsLoading(false);
    geoServiceUpdateAddress(placeDetails);
    setOnboardingModalIsOpen(false);
  };

  const grabCurrentLocation = async () => {
    setIsLoading(true);
    try {
      const currentAddress = await getAddressCurrentLocation();

      if (currentAddress) {
        updateAddress(currentAddress);
      }
    } catch (error: any) {
      const { err, friendlyMessage } = error as {
        err: GeolocationPositionError;
        friendlyMessage: string;
      };

      if (err.code) {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setLocationPermissionDenied(friendlyMessage);
            break;
          case err.POSITION_UNAVAILABLE:
          case err.TIMEOUT:
            showToast({
              icon: "alert",
              message: friendlyMessage as string,
              type: "error",
            });
            break;
        }
      } else {
        throw err;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onClickSkip = () => {
    if (addressStored.formattedAddress) {
      setOnboardingModalIsOpen(false);
      onClose && onClose();
    } else {
      setOnboardingModalStep(1);
    }
  };

  return (
    <>
      <SendPasswordResetModal
        isOpen={showPasswordResetModal}
        close={() => setShowPasswordResetModal(false)}
      />

      <Modal isOpen={onboardingModalIsOpen && !showPasswordResetModal}>
        {onBoardingModalStep === 0 ? (
          <StepOneContent>
            <Title>
              <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                <Text
                  css={{ fontSize: 28, letterSpacing: -0.3 }}
                  variant="headline"
                  color="textTitle"
                >
                  {t("stepOneTitle")}
                </Text>
                <img width={"140px"} src={LogoWordMark} alt="CityMD Logo" />
              </div>
            </Title>
            <Description>
              <Text>{t("stepOneSubtitle")}</Text>
            </Description>
            {SIGN_UP_V2_ENABLED ? (
              <StepOneFooterVertical>
                <InfoBanner
                  enabled={enabled}
                  showIcon={showIcon}
                  linkText={linkText}
                  link={link}
                  variant={variant}
                  css={{ marginBottom: 40, marginTop: 8 }}
                >
                  <Text variant="body1">{messageText}</Text>
                </InfoBanner>
                <Button
                  onClick={() => {
                    history.push(routes.signUp, { redirectPage });
                  }}
                >
                  {t("stepOneSignUp")}
                </Button>
                <TextDivider label={t("or")} />
                <Button
                  variant="secondary"
                  onClick={() => {
                    login(redirectPage);
                  }}
                >
                  {t("stepOneSignIn")}
                </Button>

                <div css={{ display: "flex", justifyContent: "space-between", marginTop: 42 }}>
                  <Button
                    css={{ fontSize: 18 }}
                    onClick={() => {
                      setShowPasswordResetModal(true);
                    }}
                    variant="borderBottom"
                  >
                    {t("forgotPassword")}
                  </Button>
                  <Button css={{ fontSize: 18 }} onClick={onClickSkip} variant="borderBottom">
                    {t("stepOneSkip")}
                  </Button>
                </div>
              </StepOneFooterVertical>
            ) : (
              <StepOneFooter>
                <Button onClick={() => login()}>{t("stepOneSignInSignUp")}</Button>
                <Button onClick={onClickSkip} variant="borderBottom">
                  {t("stepOneSkip")}
                </Button>
              </StepOneFooter>
            )}
          </StepOneContent>
        ) : (
          <StepTwoContent>
            <StepTwoHeader>
              <Title>
                <StepTwoTitle variant="title1" color="textTitle">
                  {t("stepTwoTitle")}
                </StepTwoTitle>
              </Title>

              <Description>
                <StepTwoSubtitle color="textPrimary">{t("stepTwoSubtitle")}</StepTwoSubtitle>
              </Description>
            </StepTwoHeader>
            <StepTwoFooter>
              <div css={{ position: "relative" }}>
                <Button isDisabled={isLoading} fullWidth="percent" onClick={grabCurrentLocation}>
                  {locationPermissionDenied
                    ? t("stepTwoLocationPermissionDenied")
                    : t("stepTwoLocation")}
                </Button>

                {locationPermissionDenied && (
                  <Tooltip
                    anchor={
                      <Tooltip.Anchor
                        // This explictly has to use `width: '100%', height: '100%',
                        // or it doesn't work. That is, `layout.absoluteFill` does
                        // not work for this case
                        css={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    }
                    location="top"
                  >
                    {locationPermissionDenied}
                  </Tooltip>
                )}
              </div>
              <TextDivider label={t("or")} />
              <AddressCombobox
                placeholder={t("addressPlaceholder")}
                onSelectAddress={(selectedAddress) => {
                  updateAddress(selectedAddress);
                }}
              />
            </StepTwoFooter>
          </StepTwoContent>
        )}
      </Modal>
    </>
  );
};

export default OnboardingModal;
